import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminOperations.css";
import {
  apiDescargarExcel,
  apiEliminarExcel,
  apiMostrarExcel,
} from "./constants";

export const ExcelTable = () => {
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [accionArchivo, setAccionArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const navigate = useNavigate();

  const handleMostrarArchivos = useCallback(async () => {
    setCargando(true);
    setResultado(null);

    try {
      const response = await axios.get(apiMostrarExcel);
      const lista = response.data?.data ?? response.data ?? [];
      setArchivos(Array.isArray(lista) ? lista : []);
    } catch (error) {
      console.error("Error al cargar archivos:", error);
      setArchivos([]);
      setResultado({
        tipo: "error",
        mensaje: "No fue posible cargar los archivos Excel.",
      });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    handleMostrarArchivos();
  }, [handleMostrarArchivos]);

  const handleDescargarArchivo = async (idArchivo, nombreArchivo) => {
    setAccionArchivo(idArchivo);
    setResultado(null);

    try {
      const response = await axios.get(
        `${apiDescargarExcel}?id_archivo=${idArchivo}`,
        { responseType: "blob" },
      );
      const urlTemporal = window.URL.createObjectURL(new Blob([response.data]));
      const enlace = document.createElement("a");
      enlace.href = urlTemporal;
      enlace.download = nombreArchivo;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(urlTemporal);
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      setResultado({
        tipo: "error",
        mensaje: "No fue posible descargar el archivo Excel.",
      });
    } finally {
      setAccionArchivo(null);
    }
  };

  const handleEliminarArchivo = async (idArchivo, nombreArchivo) => {
    const confirmado = window.confirm(
      `¿Deseas eliminar el archivo "${nombreArchivo}"?`,
    );
    if (!confirmado) {
      return;
    }

    setAccionArchivo(idArchivo);
    setResultado(null);

    try {
      await axios.delete(`${apiEliminarExcel}?id_archivo=${idArchivo}`);
      setArchivos((archivosActuales) =>
        archivosActuales.filter((archivo) => archivo.id !== idArchivo),
      );
      setResultado({
        tipo: "success",
        mensaje: "El archivo Excel se eliminó correctamente.",
      });
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
      setResultado({
        tipo: "error",
        mensaje:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No fue posible eliminar el archivo Excel.",
      });
    } finally {
      setAccionArchivo(null);
    }
  };

  return (
    <div className="admin-operation-page">
      <main className="admin-operation-container">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Administración de archivos</p>
          <h1>Archivos Excel</h1>
          <p>
            Consulta los archivos almacenados, descarga una copia o elimina los
            registros que ya no sean necesarios.
          </p>
        </header>

        <section className="admin-operation-panel">
          <div className="admin-operation-toolbar">
            <p className="admin-operation-count">
              {cargando
                ? "Consultando archivos..."
                : `${archivos.length} archivo${archivos.length === 1 ? "" : "s"}`}
            </p>
            <div className="admin-operation-toolbar-actions">
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={handleMostrarArchivos}
                disabled={cargando || accionArchivo !== null}
              >
                Actualizar listado
              </button>
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={() => navigate("/admin-home")}
                disabled={accionArchivo !== null}
              >
                Regresar al inicio
              </button>
            </div>
          </div>

          {resultado && (
            <div
              className={`admin-operation-status admin-operation-status-${resultado.tipo}`}
              role="status"
            >
              {resultado.mensaje}
            </div>
          )}

          {!cargando && archivos.length === 0 ? (
            <p className="admin-operation-empty">
              No hay archivos Excel registrados.
            </p>
          ) : (
            <div className="admin-operation-table-wrapper">
              <table className="admin-operation-table">
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Administrador</th>
                    <th>Fecha de carga</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {archivos.map((archivo) => (
                    <tr key={archivo.id}>
                      <td>{archivo.nombre_archivo}</td>
                      <td>{archivo.matricula_admin}</td>
                      <td>{archivo.fecha_hora_subida}</td>
                      <td>
                        <div className="admin-operation-table-actions">
                          <button
                            className="admin-operation-button admin-operation-button-compact"
                            type="button"
                            onClick={() =>
                              handleDescargarArchivo(
                                archivo.id,
                                archivo.nombre_archivo,
                              )
                            }
                            disabled={accionArchivo !== null}
                          >
                            {accionArchivo === archivo.id
                              ? "Procesando..."
                              : "Descargar"}
                          </button>
                          <button
                            className="admin-operation-button admin-operation-button-danger admin-operation-button-compact"
                            type="button"
                            onClick={() =>
                              handleEliminarArchivo(
                                archivo.id,
                                archivo.nombre_archivo,
                              )
                            }
                            disabled={accionArchivo !== null}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
